'use client';

import { Badge, Button, DataTable, useToggle } from '@commenda/kakashi';
import { TSecureInformationRequestListItem } from '@commenda/types';
import {
  NonTimezoneSensitiveDate,
  RECURRING_USER_TASK_STATUS_ENUM_TO_TEXT,
} from '@commenda/utils';
import { RecurringUserTaskStatus } from '@prisma/client';
import { useState } from 'react';
import { getServiceRequestServiceDetails } from '../../service-request/constants';
import { SecureInformationRequestAnswersSummaryModal } from './SecureInformationRequestSummaryModal';

type SecureInformationRequestsTableProps = {
  data: TSecureInformationRequestListItem[];
  isLoading?: boolean;
  onRowClick?: (row: TSecureInformationRequestListItem) => void;
  showLinkedService: boolean;
};

const SecureInformationRequestsTable = ({
  data,
  isLoading = false,
  onRowClick,
  showLinkedService,
}: SecureInformationRequestsTableProps) => {
  const defaultVisibleColumns = [
    'requestName',
    'status',
    'sender',
    'sendDate',
    'replyDate',
    'linkedService',
    'actions',
  ];

  const secureInformationRequestSummaryModalVisibility = useToggle();

  const [
    selectedSecureInformationRequestId,
    setSelectedSecureInformationRequestId,
  ] = useState<string | null>(null);

  const flattenedRows = data.map((row, index) => ({
    id: row.id || index,
    requestName: row.title || 'N/A',
    linkedService:
      getServiceRequestServiceDetails(row.serviceRequest).displayName || 'N/A',
    status: RECURRING_USER_TASK_STATUS_ENUM_TO_TEXT[row.status],
    originalStatus: row.status,
    sender: row.createdBy?.name || 'N/A',
    sendDate: row.createdAt,
    replyDate:
      row.status === RecurringUserTaskStatus.COMPLETED
        ? row.lastModifiedAt
        : 'N/A',
    originalData: row,
  }));

  const columnGroups = [
    {
      id: 'request-details',
      columns: [
        {
          accessorKey: 'requestName',
          header: 'Request name',
          title: 'Request name',
          cell: ({ row }) => {
            return (
              <p className="txt-small-medium text-text-primary">
                {row.original.requestName || 'N/A'}
              </p>
            );
          },
        },
        ...(showLinkedService
          ? [
              {
                accessorKey: 'linkedService',
                header: 'Linked Service',
                title: 'Linked Service',
                cell: ({ row }) => {
                  return (
                    <p className="txt-small-medium text-text-primary">
                      {row.original.linkedService || 'N/A'}
                    </p>
                  );
                },
              },
            ]
          : []),
        {
          accessorKey: 'status',
          header: 'Status',
          title: 'Status',
          cell: ({ row }) => {
            const status = row.original.originalStatus;
            return (
              <Badge
                size="sm"
                variant={
                  status === RecurringUserTaskStatus.COMPLETED
                    ? 'success'
                    : status === RecurringUserTaskStatus.INPROGRESS
                      ? 'warning'
                      : 'gray'
                }
              >
                {row.original.status}
              </Badge>
            );
          },
        },
        {
          accessorKey: 'sender',
          header: 'Sender (agent)',
          title: 'Sender (agent)',
          cell: ({ row }) => {
            return (
              <p className="txt-small-medium text-text-primary">
                {row.original.sender || 'N/A'}
              </p>
            );
          },
        },
        {
          accessorKey: 'sendDate',
          header: 'Send date',
          title: 'Send date',
          cell: ({ row }) => {
            const { sendDate } = row.original;
            return (
              <p className="txt-small-medium text-text-primary">
                {sendDate
                  ? NonTimezoneSensitiveDate.fromNativeDate(
                      new Date(sendDate)
                    ).format('MMM DD, YYYY')
                  : 'N/A'}
              </p>
            );
          },
        },
        {
          accessorKey: 'replyDate',
          header: 'Reply date',
          title: 'Reply date',
          cell: ({ row }) => {
            const { replyDate } = row.original;
            return (
              <p className="txt-small-medium text-text-primary">
                {replyDate && replyDate !== 'N/A'
                  ? NonTimezoneSensitiveDate.fromNativeDate(
                      new Date(replyDate)
                    ).format('MMM DD, YYYY')
                  : 'N/A'}
              </p>
            );
          },
        },
      ],
    },
    {
      id: 'actions',
      sticky: 'right' as const,
      columns: [
        {
          accessorKey: 'actions',
          header: 'Actions',
          title: 'Actions',
          cell: ({ row }) => {
            return (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setSelectedSecureInformationRequestId(row.original.id);
                  secureInformationRequestSummaryModalVisibility.show();
                }}
              >
                View details
              </Button>
            );
          },
        },
      ],
    },
  ];

  return (
    <>
      <SecureInformationRequestAnswersSummaryModal
        isOpen={secureInformationRequestSummaryModalVisibility.value}
        onClose={secureInformationRequestSummaryModalVisibility.off}
        secureInformationRequestId={selectedSecureInformationRequestId}
      />
      <DataTable
        columnGroups={columnGroups}
        data={flattenedRows}
        isLoading={isLoading}
        onRowClick={
          onRowClick ? (row) => onRowClick(row.originalData) : undefined
        }
        tableViewConfig={{
          isEnabled: false,
          defaultView: 'regular',
        }}
        searchConfig={{
          isEnabled: true,
          placeholder: 'Search by request name or sender',
          columnsToEnableSearchFor: ['requestName', 'sender'],
        }}
        paginationConfig={{
          isEnabled: false,
          type: 'PAGE',
          pageSize: 100,
        }}
        columnSelectorConfig={{
          isEnabled: true,
          placeholder: 'Customize columns',
          searchText: 'Search columns',
          notFoundText: 'No columns found',
          selectAllText: 'Show all columns',
          defaultVisibleColumns,
        }}
        emptyStateConfig={{
          title: 'No secure information requests found',
          description:
            'Create your first secure information request to get started.',
        }}
      />
    </>
  );
};

export { SecureInformationRequestsTable };
